import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { useGetPropertyFields } from '@/modules/settings/property/apiHooks';
import { usePropertySettings } from '@/modules/settings/property/components/logic';
import { IPropertySettings } from '@/modules/settings/property/types';
import { Table } from '@/shared/components/atoms';
import { EmptyCard } from '@/shared/components/molecules';
import { EntityType } from '@/shared/constant/enums';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';

import FarmGirl from '~/images/shared/FarmGirl.svg';

const PropertyFieldsTable = ({
  isArchived = false,
}: {
  isArchived?: boolean;
}) => {
  const { t } = useTranslation();

  const { push } = useRouter();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { getPropertyFieldsTable } = usePropertySettings();

  const [pageNumber, setPageNumber] = useState(1);

  const { data } = useGetPropertyFields({
    propertyId: selectedAsset?.id,
    pageNumber,
    pageSize: 10,
    isArchived,
  });

  const isFarm = selectedAsset?.entityTypeId === EntityType.FARM;

  return data && data.propertyFields.length && selectedAsset?.id ? (
    <Table<IPropertySettings.IPropertyField>
      pagination={true}
      currentPage={pageNumber}
      totalCount={data?.totalCount ?? 0}
      onPageChange={(pageNumber) => {
        setPageNumber(pageNumber);
      }}
      perPage={10}
      selection={false}
      data={data?.propertyFields ?? []}
      columns={getPropertyFieldsTable()}
      columnVisibility={{
        varietyName: isFarm,
        area: isFarm,
        updatedAt: isArchived,
        archivedByName: isArchived,
        fieldActions: !isArchived,
      }}
      footerClassName='bg-neutralVariant-95'
      headCellClassName='text-start border-b-xs border-neutralVariant-90 text-surface-10 font-semi-bold'
      rowCellClassName='border-b-xs border-neutralVariant-90'
      headContainerClassName='!bg-background-neutral-99'
      tableWrapperClassName='!rounded-xl border-xs border-outline-variant'
      rowContainerClassName={classNames(!isArchived && 'cursor-pointer')}
      onClickRow={
        !isArchived
          ? (row) =>
              push({
                pathname: `${routes.propertySettings}/[id]/[field]`,
                query: {
                  id: selectedAsset.id,
                  field: row.name,
                  fieldId: row.id,
                },
              })
          : undefined
      }
    />
  ) : data && data.propertyFields.length === 0 ? (
    <EmptyCard
      fullSVG
      variant='icon'
      vector={<FarmGirl />}
      message={t(
        isArchived
          ? 'no-archived-fields'
          : 'Add your fields to start your adventure!'
      )}
      directiveMessage={
        !isArchived && `${t('Click “Create new field” button to start')} ..`
      }
    />
  ) : null;
};

export default PropertyFieldsTable;
